import { useCallback, useEffect, useState } from "react";
import { Alert, AlertColor, Box, Button, Typography } from "@mui/material";
import { PaymentMethodPreview, PaymentMethodPlaceholder } from "../../../../../components/Shared/PaymentMethodPreview";
import PaymentHistoryTable from "../../../../../components/Shared/PaymentHistoryTable";
import paymentService from "../../../../../services/payment.service";
import authService from "../../../../../services/auth.service";
import { useUser } from "../../../../../hooks/useUser";
import { usePaymentStore } from "../../../../../config/advertiser.stores";
import { useSearchParams } from "react-router-dom";

import { useStripe } from "@stripe/react-stripe-js";

const BillingSettings = () => {
    const user = useUser();
    const stripe = useStripe();

    const paymentStatus = usePaymentStore((state) => state.paymentStatus);
    const setPaymentStatus = usePaymentStore((state) => state.setPaymentStatus);
    const paymentHistory = usePaymentStore((state) => state.paymentHistory);
    const setPaymentHistory = usePaymentStore((state) => state.setPaymentHistory);

    const [isLoading, setIsLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const [alert, setAlert] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");

    const fetchStatusData = useCallback(async () => {
        setIsLoading(true);
        const response = await authService.status(user.id);
        setIsLoading(false);
        setPaymentStatus(response);
    }, [user.id, setPaymentStatus]);

    useEffect(() => {
        fetchStatusData();
    }, [fetchStatusData]);

    useEffect(() => {
        if (searchParams.get("success") === "true") {
            setSeverity("success");
            setAlert("Successfully added payment method to account");
        } else if (searchParams.get("failure") === "true") {
            setSeverity("error");
            setAlert("There was an error adding a payment method.");
        }
    }, [searchParams]);

    async function modifyPaymentMethod() {
        const sessionId = await paymentService.createSession(user.id);
        if (sessionId.length === 0) {
            setSeverity("error");
            setAlert("There was an error setting up a session to modify payment method. Please try again later.");
        } else {
            stripe?.redirectToCheckout({ sessionId: sessionId });
        }
    };
    
    return (
        <Box display="flex" flexDirection="column" gap={3}>
            { alert !== "" && 
                <Alert
                    severity={severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                    onClose={() => { setAlert("") }}
                >
                    {alert}
                </Alert>
            }
            <Typography variant="h5">Payment Method</Typography>
            {paymentStatus === "active" ? (
                <Box display="flex" gap={3}>
                    <PaymentMethodPreview />
                    <Box display="flex" flexDirection="column" gap={3}>
                        <Typography marginTop="auto" component="pre">
                            You currently have a card added to your account.{"\n"}
                            You may change it at anytime before your next bill is charged.
                        </Typography>
                        <Button
                            onClick={modifyPaymentMethod}
                            variant="contained"
                            sx={{ width: '290px' }}
                            disabled={isLoading}
                        >
                            Change Payment Method
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box display="flex" gap={3}>
                    <PaymentMethodPlaceholder />
                    <Box display="flex" flexDirection="column" gap={3}>
                        <Typography marginTop="auto" component="pre">
                            To create ads you must first set up a payment method.{"\n"}
                            We will not charge you anything for adding your card to your account.{"\n"}
                            If a card you previously added isn't appearing, there was an error validating it.
                            Please try adding it again.
                        </Typography>
                        <Button
                            onClick={modifyPaymentMethod}
                            variant="contained"
                            sx={{ width: '290px' }}
                            disabled={isLoading}
                        >
                            Add Payment Method
                        </Button>
                    </Box>
                </Box>
            )}
            <Typography variant="h5">Payment History</Typography>
            <PaymentHistoryTable
                history={paymentHistory}
                setHistory={setPaymentHistory}
                queryHistory={paymentService.queryHistory}
            />
        </Box>
    )
}

export default BillingSettings;
